import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Snackbar, Alert, Tooltip, Stack,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { api } from "../../api";

/**
 * FEAT-044 — Portal role-management (governance) screen (GH #118).
 * Drives the backend /portal/crud/role(s) endpoint (Phase B). Admin-gated by the `role`
 * capability (disclosureAdmin / districtAdmin today). Built-in (SYSTEM) roles are read-only;
 * per-tenant custom roles can be created / edited / deleted; roles can be assigned to a user.
 *
 * NOTE: keep CAPABILITY_CATALOG in sync with sam/backend/disclosure/base/roles.py CAPABILITY_CATALOG.
 */
const CAPABILITY_CATALOG = [
  "incident", "incidentactivity", "notification", "comment", "message", "paidtimeoff",
  "dashboard", "student", "user", "broadcast", "school", "district", "identityaccesslog", "role",
];

// Grant value semantics mirror roles.py: true=allow; "ownSchoolOne"/"ownDistrictOne"=own-scope-only.
const GRANT_OPTIONS = [
  { value: "none", label: "No access" },
  { value: "true", label: "Allow" },
  { value: "ownSchoolOne", label: "Own school only" },
  { value: "ownDistrictOne", label: "Own district only" },
];

const grantChipLabel = (val) =>
  val === true || val === "true" ? "allow"
    : val === "ownSchoolOne" ? "own-school"
    : val === "ownDistrictOne" ? "own-district" : String(val);

const isBuiltin = (role) => role?.builtin === true || role?.tenantId === "SYSTEM";

const emptyForm = () => ({ roleId: "", name: "", grants: {} });

const Roles = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuper = user.permission === "disclosureAdmin";
  const canManage = user.permission === "disclosureAdmin" || user.permission === "districtAdmin";

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [tenant, setTenant] = useState(isSuper ? "" : (user.district || ""));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignUser, setAssignUser] = useState("");
  const [assignRoleIds, setAssignRoleIds] = useState([]);

  const [toast, setToast] = useState({ open: false, severity: "success", msg: "" });
  const notify = (severity, msg) => setToast({ open: true, severity, msg });

  const effectiveTenant = isSuper ? tenant : (user.district || "");

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    const params = effectiveTenant ? { tenantId: effectiveTenant } : {};
    const res = await api("get", params, "roles");
    if (res?.status === 200 && res?.data?.data) {
      setRoles(res.data.data);
    } else if (res?.error) {
      notify("error", res?.data?.message || "Could not load roles.");
      setRoles([]);
    } else {
      setRoles(res?.data?.data || []);
    }
    setLoading(false);
  }, [effectiveTenant]);

  useEffect(() => {
    if (!canManage) return;
    if (isSuper) {
      // load districts so the super-admin can pick which tenant's roles to manage
      (async () => {
        const res = await api("get", {}, "districts");
        const rows = res?.data?.data || [];
        setDistricts(rows.map((d) => ({ id: d.id, name: d.name || d.districtName || d.id })));
      })();
    }
  }, [canManage, isSuper]);

  useEffect(() => {
    if (canManage) fetchRoles();
  }, [canManage, fetchRoles]);

  if (!canManage) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5">Roles</Typography>
        <Typography sx={{ mt: 2 }} color="text.secondary">
          You do not have permission to manage roles.
        </Typography>
      </Box>
    );
  }

  const openCreate = () => {
    if (isSuper && !tenant) {
      notify("warning", "Select a district first — a custom role must belong to a tenant.");
      return;
    }
    setEditing(false);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditing(true);
    setForm({ roleId: role.roleId, name: role.name || "", grants: { ...(role.grants || {}) } });
    setDialogOpen(true);
  };

  const setGrant = (cap, value) => {
    setForm((f) => {
      const grants = { ...f.grants };
      if (value === "none") delete grants[cap];
      else grants[cap] = value === "true" ? true : value;
      return { ...f, grants };
    });
  };

  const saveRole = async () => {
    if (!form.roleId.trim() || !form.name.trim()) {
      notify("warning", "Role id and name are required.");
      return;
    }
    if (Object.keys(form.grants).length === 0) {
      notify("warning", "Grant at least one capability.");
      return;
    }
    setSaving(true);
    const payload = {
      tenantId: effectiveTenant,
      roleId: form.roleId.trim(),
      name: form.name.trim(),
      grants: form.grants,
    };
    const res = await api(editing ? "patch" : "post", payload, "role");
    setSaving(false);
    if (res?.status === 200 && res?.data?.success !== false) {
      notify("success", editing ? "Role updated." : "Role created.");
      setDialogOpen(false);
      fetchRoles();
    } else {
      notify("error", res?.data?.message || "Could not save the role.");
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete custom role "${role.name || role.roleId}"? This cannot be undone.`)) return;
    const res = await api("delete", { tenantId: role.tenantId, roleId: role.roleId }, "role");
    if (res?.status === 200 && res?.data?.success !== false) {
      notify("success", "Role deleted.");
      fetchRoles();
    } else {
      notify("error", res?.data?.message || "Could not delete the role.");
    }
  };

  // A person's effective roles = assigned roleIds, else their legacy single `permission` (the
  // fallback the policy engine itself uses until roleIds are set).
  const currentRolesOf = (u) =>
    u?.roleIds && u.roleIds.length ? u.roleIds : (u?.permission ? [u.permission] : []);

  const openAssign = async () => {
    setAssignUser("");
    setAssignRoleIds([]);
    setAssignOpen(true);
    const res = await api("get", {}, "users");
    setUsers(res?.data?.data || []);
  };

  const onPickAssignUser = (id) => {
    setAssignUser(id);
    // Pre-fill with the user's CURRENT roles so this dialog edits (not blindly overwrites) them.
    setAssignRoleIds(currentRolesOf(users.find((u) => u.id === id)));
  };

  const saveAssign = async () => {
    if (!assignUser || assignRoleIds.length === 0) {
      notify("warning", "Pick a user and at least one role.");
      return;
    }
    const res = await api("post", { targetUserId: assignUser, roleIds: assignRoleIds }, "role");
    if (res?.status === 200 && res?.data?.success !== false) {
      notify("success", "Roles assigned.");
      // Reflect the change locally so reopening the dialog shows the new roles.
      setUsers((prev) => prev.map((u) => (u.id === assignUser ? { ...u, roleIds: assignRoleIds } : u)));
      setAssignOpen(false);
    } else {
      notify("error", res?.data?.message || "Could not assign roles.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="h4">Roles</Typography>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          {isSuper && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="tenant-label">District (tenant)</InputLabel>
              <Select
                labelId="tenant-label"
                label="District (tenant)"
                value={tenant}
                onChange={(e) => setTenant(e.target.value)}
              >
                <MenuItem value=""><em>Built-in roles only</em></MenuItem>
                {districts.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button variant="outlined" startIcon={<PersonAddAltOutlinedIcon />} onClick={openAssign}>
            Assign to user
          </Button>
          <Button variant="contained" onClick={openCreate}>Add role</Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        Built-in roles are provided out of the box and cannot be edited. Create custom roles to model
        your own titles (e.g. Counselor, SRO) by granting capabilities.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role id</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Capabilities</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  {loading ? "Loading…" : "No roles to show."}
                </TableCell>
              </TableRow>
            )}
            {roles.map((role) => {
              const builtin = isBuiltin(role);
              const grants = role.grants || {};
              return (
                <TableRow key={`${role.tenantId}#${role.roleId}`} hover>
                  <TableCell>{role.name || role.roleId}</TableCell>
                  <TableCell><code>{role.roleId}</code></TableCell>
                  <TableCell>
                    <Chip size="small" label={builtin ? "Built-in" : "Custom"}
                      color={builtin ? "default" : "primary"} variant={builtin ? "outlined" : "filled"} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 480 }}>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                      {Object.keys(grants).length === 0
                        ? <Typography variant="caption" color="text.secondary">—</Typography>
                        : Object.entries(grants).map(([cap, val]) => (
                            <Chip key={cap} size="small" variant="outlined"
                              label={`${cap}: ${grantChipLabel(val)}`} />
                          ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {builtin ? (
                      <Tooltip title="Built-in roles are read-only"><span>
                        <IconButton disabled size="small"><EditOutlinedIcon fontSize="small" /></IconButton>
                      </span></Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(role)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteRole(role)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit role" : "Add custom role"}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Role id" size="small" value={form.roleId} disabled={editing}
              helperText={editing ? "Role id can't be changed" : "Lowercase identifier, e.g. counselor"}
              onChange={(e) => setForm((f) => ({ ...f, roleId: e.target.value }))}
            />
            <TextField
              label="Display name" size="small" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Capabilities</Typography>
            {CAPABILITY_CATALOG.map((cap) => {
              const current = form.grants[cap];
              const selected = current === true ? "true" : (current || "none");
              return (
                <Stack key={cap} direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                  <Typography variant="body2">{cap}</Typography>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select value={selected} onChange={(e) => setGrant(cap, e.target.value)}>
                      {GRANT_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRole} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create role"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign roles to a user</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="assign-user-label">User</InputLabel>
              <Select
                labelId="assign-user-label" label="User" value={assignUser}
                onChange={(e) => onPickAssignUser(e.target.value)}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {(u.firstName || "") + " " + (u.lastName || "")} {u.email ? `(${u.email})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {assignUser && (
              <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                <Typography variant="caption" color="text.secondary">Current roles:</Typography>
                {currentRolesOf(users.find((u) => u.id === assignUser)).map((r) => (
                  <Chip key={r} size="small" variant="outlined" label={r} />
                ))}
              </Stack>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel id="assign-roles-label">Roles</InputLabel>
              <Select
                labelId="assign-roles-label" label="Roles" multiple value={assignRoleIds}
                onChange={(e) => setAssignRoleIds(
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value
                )}
                renderValue={(sel) => sel.join(", ")}
              >
                {roles.map((r) => (
                  <MenuItem key={r.roleId} value={r.roleId}>{r.name || r.roleId}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Assigning roles switches this user to role-based permissions (their legacy single role is
              kept as a fallback).
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAssign}>Assign</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open} autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles;
